/**
 * FR-018.7 — the "Zuordnung nötig" card on a parked object (quarantine stub),
 * TENANT_CONFIGURATION_UI.md §4 / suggestions §6.4.
 *
 * Top to bottom: (1) one sentence naming the source system and the master role,
 * (2) the parked counts from the stub's own `unassigned` row (omitted when absent —
 * never fabricated), (3) a radio group: ranked suggestions (tier word + one evidence
 * line, never a number), then "<noun> auswählen …" with a search, then "Ist ein eigenes
 * Objekt" (explains the master route; it has no confirm), (4) `gilt ab Erntejahr` for
 * season kinds, (5) `Später` · `Auswirkung prüfen` → the mapping editor, pre-filled.
 *
 * The card reads only; every write goes through the editor's preview + confirm.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SourceRowDto, SourceSearchHit, SourcesResponse, Suggestion } from '../../../../services/sourcesService';
import { useSourceSearch, useSourceSuggestions } from '../../../../hooks/useSources';
import { useSourceSystems } from '../../../../hooks/useSourceSystems';
import type { EditorIntent } from './SourcesPanel';
import { isSeasonalEditor } from './editorItems';
import './NeedsAssignmentCard.css';

type Translate = (key: string, def: string, opts?: Record<string, unknown>) => string;

export interface NeedsAssignmentCardProps {
    sources: SourcesResponse;
    onOpenEditor(intent: EditorIntent): void;
}

const SEARCH_DEBOUNCE_MS = 250;
const numberFmt = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });

type Target = NonNullable<EditorIntent['target']>;

function yearOf(date: string | null | undefined): number | null {
    const m = typeof date === 'string' ? /^(\d{4})/.exec(date) : null;
    return m ? Number(m[1]) : null;
}

/** The parked counts of the stub's `unassigned` row, as one phrase; `null` when the server sent none. */
export function parkedCountsPhrase(counts: SourceRowDto['counts'] | undefined, t: Translate): string | null {
    if (!counts) return null;
    const parts: string[] = [];
    if (typeof counts.picks === 'number') {
        parts.push(
            t(counts.picks === 1 ? 'farm.sources.counts.picks_one' : 'farm.sources.counts.picks_other', '{{n}} Pflückungen', {
                n: numberFmt.format(counts.picks),
            }),
        );
    }
    if (typeof counts.kg === 'number') {
        parts.push(t('farm.sources.counts.kg', '{{kg}} kg', { kg: numberFmt.format(counts.kg) }));
    }
    if (typeof counts.documents === 'number') {
        parts.push(
            t(
                counts.documents === 1 ? 'farm.sources.counts.documents_one' : 'farm.sources.counts.documents_other',
                '{{n}} Belege',
                { n: numberFmt.format(counts.documents) },
            ),
        );
    }
    return parts.length > 0 ? parts.join(' · ') : null;
}

/** One evidence line for a suggestion: why it matches, then the shared crop and the area. */
export function evidenceLine(s: Suggestion, t: Translate): string {
    const p = s.evidence_params ?? {};
    const tokens = (Array.isArray(p.tokens) ? p.tokens : []).filter((x): x is string => typeof x === 'string' && x !== '');
    // Tokens only when there are some; an unknown key gets the generic label — never "Ähnlicher Name ()".
    const why =
        s.evidence_key === 'same_name'
            ? t('farm.sources.evidence.same_name', 'Gleicher Name')
            : s.evidence_key === 'similar_name' && tokens.length > 0
              ? t('farm.sources.evidence.similar_name', 'Ähnlicher Name ({{tokens}})', { tokens: tokens.join(', ') })
              : t('farm.sources.evidence.similar_name_generic', 'Ähnlicher Name');
    const crop = [p.crop, p.variety].filter((x): x is string => !!x).join(' ');
    const area = typeof p.area_ha === 'number' ? t('farm.sources.editor.area', '{{ha}} ha', { ha: numberFmt.format(p.area_ha) }) : null;
    return [why, crop || null, area].filter((x): x is string => !!x).join(' · ');
}

export const NeedsAssignmentCard: React.FC<NeedsAssignmentCardProps> = ({ sources, onOpenEditor }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as unknown as Translate;
    const uid = React.useId();
    const systems = useSourceSystems();
    const { object, config, master, rows } = sources;
    const entityType = object.entity_type;
    const entityId = object.entity_id;
    const noun = config.noun;

    const systemName = (id: string | null | undefined) =>
        (id && systems.find((s) => s.id === id)?.display_name) || id || '';
    const unassigned = rows.find((r) => r.kind === 'unassigned');
    const stubSystem = systemName(object.stub?.source_system ?? unassigned?.source_system);
    const masterSystem = 'source_system' in master ? systemName(master.source_system) : '';
    const masterRole = config.master_role;
    const masterPhrase = masterRole ? (masterSystem ? `${masterSystem} (${masterRole})` : masterRole) : null;

    const suggestionsQuery = useSourceSuggestions(entityType, entityId, config.suggestions === true);
    const suggestions = config.suggestions ? (suggestionsQuery.data ?? []) : [];

    const [collapsed, setCollapsed] = React.useState(false);
    const [choice, setChoice] = React.useState<string | null>(null);
    const [q, setQ] = React.useState('');
    const [debouncedQ, setDebouncedQ] = React.useState('');
    const [picked, setPicked] = React.useState<SourceSearchHit | null>(null);

    const seasonal = unassigned ? unassigned.validity_kind === 'season' : isSeasonalEditor('assign', undefined, entityType);
    const currentYear = new Date().getFullYear();
    const defaultYear = unassigned?.valid_from ?? yearOf(object.stub?.first_seen) ?? currentYear;
    const [year, setYear] = React.useState<number>(defaultYear);

    React.useEffect(() => {
        const h = window.setTimeout(() => setDebouncedQ(q.trim()), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(h);
    }, [q]);
    const searchQ = choice === 'other' && debouncedQ.length >= 2 ? debouncedQ : '';
    const search = useSourceSearch(entityType, searchQ);

    const title = t('farm.sources.stub.cardTitle', 'Zuordnung nötig');

    if (collapsed) {
        return (
            <section className="needs-assignment needs-assignment--collapsed" data-testid="needs-assignment-card" aria-label={title}>
                <span className="needs-assignment__reminder">
                    <strong>{title}</strong> · {t('farm.sources.stub.reminder', 'Wartet noch auf Zuordnung.')}
                </span>
                <button type="button" className="needs-assignment__btn needs-assignment__btn--link" onClick={() => setCollapsed(false)}>
                    {t('farm.sources.stub.show', 'Anzeigen')}
                </button>
            </section>
        );
    }

    let target: Target | null = null;
    if (choice === 'other' && picked) {
        target = { entity_id: picked.entity_id, name: picked.name, properties: picked.properties };
    } else if (choice?.startsWith('s:')) {
        const s = suggestions.find((x) => `s:${x.entity_id}` === choice);
        if (s) target = { entity_id: s.entity_id, name: s.name, properties: s.properties };
    }

    const sentence = masterPhrase
        ? t('farm.sources.stub.sentence', '{{system}} meldet dieses Objekt ({{noun}}), das {{master}} noch nicht kennt.', {
              system: stubSystem,
              master: masterPhrase,
              noun,
          })
        : t(
              'farm.sources.stub.sentenceNoMaster',
              '{{system}} meldet dieses Objekt ({{noun}}); es ist noch keinem bestehenden Objekt zugeordnet.',
              { system: stubSystem, noun },
          );
    const counts = parkedCountsPhrase(unassigned?.counts, t);

    const years: number[] = [];
    for (let y = Math.min(defaultYear, currentYear) - 2; y <= Math.max(defaultYear, currentYear) + 1; y++) years.push(y);

    const radioName = `${uid}-choice`;
    const hits = search.data ?? [];

    const review = () => {
        if (!target) return;
        const intent: EditorIntent = { mode: 'assign', row: unassigned, entityType, entityId, target };
        if (seasonal) intent.validFrom = year;
        onOpenEditor(intent);
    };

    return (
        <section className="needs-assignment" data-testid="needs-assignment-card" aria-labelledby={`${uid}-title`}>
            <h3 id={`${uid}-title`} className="needs-assignment__title">
                {title}
            </h3>
            <p className="needs-assignment__sentence" data-testid="needs-assignment-sentence">
                {sentence}
            </p>
            {counts && (
                <p className="needs-assignment__counts" data-testid="needs-assignment-counts">
                    {t('farm.sources.stub.parked', 'Geparkt: {{counts}}', { counts })}
                </p>
            )}

            <fieldset className="needs-assignment__choices">
                <legend className="needs-assignment__legend">{t('farm.sources.stub.choicesLabel', 'Zuordnen zu')}</legend>
                {suggestions.map((s) => {
                    const value = `s:${s.entity_id}`;
                    return (
                        <label key={s.entity_id} className="needs-assignment__option" data-testid={`needs-assignment-option-${s.entity_id}`}>
                            <input
                                type="radio"
                                name={radioName}
                                value={value}
                                checked={choice === value}
                                onChange={() => setChoice(value)}
                            />
                            <span className="needs-assignment__option-body">
                                <span className="needs-assignment__option-head">
                                    <span className="needs-assignment__option-name">{s.name}</span>
                                    <span className={`needs-assignment__tier needs-assignment__tier--${s.tier}`}>
                                        {s.tier === 'strong'
                                            ? t('farm.sources.tier.strong', 'Wahrscheinlich')
                                            : t('farm.sources.tier.possible', 'Möglich')}
                                    </span>
                                </span>
                                <span className="needs-assignment__evidence">{evidenceLine(s, t)}</span>
                            </span>
                        </label>
                    );
                })}

                <label className="needs-assignment__option">
                    <input type="radio" name={radioName} value="other" checked={choice === 'other'} onChange={() => setChoice('other')} />
                    <span className="needs-assignment__option-body">
                        <span className="needs-assignment__option-name">
                            {t('farm.sources.stub.otherTarget', '{{noun}} auswählen …', { noun })}
                        </span>
                    </span>
                </label>
                {choice === 'other' && (
                    <div className="needs-assignment__search">
                        {picked && (
                            <p className="needs-assignment__picked" data-testid="needs-assignment-picked">
                                {t('farm.sources.editor.chosen', 'Ausgewählt')}: <strong>{picked.name}</strong>
                            </p>
                        )}
                        <input
                            type="search"
                            aria-label={t('farm.sources.editor.searchLabel', 'Suche')}
                            placeholder={t('farm.sources.editor.searchPlaceholder', '{{noun}} suchen …', { noun })}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                        {q.trim().length > 0 && q.trim().length < 2 && (
                            <p className="needs-assignment__meta">{t('farm.sources.editor.searchHint', 'Mindestens zwei Zeichen eingeben.')}</p>
                        )}
                        {searchQ && search.error ? (
                            <p className="needs-assignment__meta">
                                {t('farm.sources.errors.sources_search_unavailable', 'Die Suche ist gerade nicht verfügbar.')}
                            </p>
                        ) : searchQ && search.data && hits.length === 0 ? (
                            <p className="needs-assignment__meta">{t('farm.sources.editor.noHits', 'Keine Treffer.')}</p>
                        ) : null}
                        {searchQ && hits.length > 0 && (
                            <ul className="needs-assignment__hits">
                                {hits.map((hit) => (
                                    <li key={hit.entity_id}>
                                        <button
                                            type="button"
                                            className="needs-assignment__hit"
                                            onClick={() => {
                                                setPicked(hit);
                                                setQ('');
                                                setDebouncedQ('');
                                            }}
                                        >
                                            <span>{hit.name}</span>
                                            {typeof hit.properties?.area_ha === 'number' && (
                                                <span className="needs-assignment__meta">
                                                    {t('farm.sources.editor.area', '{{ha}} ha', { ha: numberFmt.format(hit.properties.area_ha) })}
                                                </span>
                                            )}
                                            {hit.properties?.crop && <span className="needs-assignment__meta">{hit.properties.crop}</span>}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                <label className="needs-assignment__option">
                    <input type="radio" name={radioName} value="own" checked={choice === 'own'} onChange={() => setChoice('own')} />
                    <span className="needs-assignment__option-body">
                        <span className="needs-assignment__option-name">{t('farm.sources.stub.ownTarget', 'Ist ein eigenes Objekt')}</span>
                    </span>
                </label>
                {choice === 'own' && (
                    <p className="needs-assignment__hint" data-testid="needs-assignment-own-hint">
                        {masterPhrase
                            ? t('farm.sources.stub.ownTargetHint', 'Legen Sie es in {{master}} an – beim nächsten Abgleich wird es hier vorgeschlagen.', {
                                  master: masterPhrase,
                              })
                            : t(
                                  'farm.sources.stub.ownTargetHintNoMaster',
                                  'Legen Sie es im führenden System an – beim nächsten Abgleich wird es hier vorgeschlagen.',
                              )}
                    </p>
                )}
            </fieldset>

            {seasonal && (
                <div className="needs-assignment__year">
                    <label htmlFor={`${uid}-year`}>{t('farm.sources.stub.validFrom', 'gilt ab Erntejahr')}</label>
                    <select id={`${uid}-year`} value={String(year)} onChange={(e) => setYear(Number(e.target.value))}>
                        {years.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="needs-assignment__footer">
                <button type="button" className="needs-assignment__btn needs-assignment__btn--outline" onClick={() => setCollapsed(true)}>
                    {t('farm.sources.stub.later', 'Später')}
                </button>
                <button
                    type="button"
                    className="needs-assignment__btn needs-assignment__btn--primary"
                    disabled={!target}
                    onClick={review}
                >
                    {t('farm.sources.actions.checkImpact', 'Auswirkung prüfen')}
                </button>
            </div>
        </section>
    );
};

export default NeedsAssignmentCard;
